import { forceNextTick } from "@/util/force-next-tick";
import { registerAllStoreActions } from "@/store/actions/registrations";
import defensiveCopy from "@/util/defensive-copy";
import {
  OnMountedElement,
  OnAfterDeletedElement,
  OnMovedElement,
  OnPropertyChange,
  OnCreatedElement,
  OnBeforeDeletingElement,
  OnActivatedElement
} from "@/types/store-actions";

type OnAfterDeletedElementCallback = (args: OnAfterDeletedElement) => void;
type OnPropertyChangeCallback = (args: OnPropertyChange) => void;
type OnMovedElementCallback = (args: OnMovedElement) => void;
type OnMountedElementCallback = (args: OnMountedElement) => void;
type OnCreatedElementCallback = (args: OnCreatedElement) => void;
type OnBeforeDeletingElementCallback = (args: OnBeforeDeletingElement) => void;
type OnActivatedElementCallback = (args: OnActivatedElement) => void;

type AllCallbacksUnion =
  | OnAfterDeletedElementCallback
  | OnPropertyChangeCallback
  | OnMovedElementCallback
  | OnMountedElementCallback
  | OnCreatedElementCallback
  | OnBeforeDeletingElementCallback
  | OnActivatedElementCallback;

type StoreActionCallbacks = {
  onAfterDeletedElement: Array<OnAfterDeletedElementCallback>;
  onPropertyChange: Array<OnPropertyChangeCallback>;
  onMovedElement: Array<OnMovedElementCallback>;
  onCreatedElement: Array<OnCreatedElementCallback>;
  onMountedElement: Array<OnMountedElementCallback>;
  onBeforeDeletingElement: Array<OnMountedElementCallback>;
  onActivatedElement: Array<OnActivatedElementCallback>;

  immediateOnAfterDeletedElement: Array<OnAfterDeletedElementCallback>;
  immediateOnPropertyChange: Array<OnPropertyChangeCallback>;
  immediateOnMovedElement: Array<OnMovedElementCallback>;
  immediateOnMountedElement: Array<OnMountedElementCallback>;
  immediateOnCreatedElement: Array<OnCreatedElementCallback>;
};

/* This API enables actions to happen to an element
when something affects it, its children, or its siblings.

For example, in a Columns element all columns must have a combined width of 100%.

So when a new Column element is added, dragged in, deleted, or
modified, the other Column elements must react and change their widths to ensure
the widths remain at 100%.

Another example is a Slider. If a new element is added the slider code needs to
run again to re-calculate its width, etc.
*/

const initialCallbacks: StoreActionCallbacks = {
  onAfterDeletedElement: [],
  immediateOnAfterDeletedElement: [],

  onPropertyChange: [],
  immediateOnPropertyChange: [],

  onMovedElement: [],
  immediateOnMovedElement: [],

  onMountedElement: [],
  immediateOnMountedElement: [],

  onCreatedElement: [],
  immediateOnCreatedElement: [],

  onBeforeDeletingElement: [],

  onActivatedElement: []
};

class BreakdanceStoreActions {
  callbacks = defensiveCopy(initialCallbacks);

  resetAllCallbacks = () => {
    this.callbacks = defensiveCopy(initialCallbacks);
  };

  registerAllStoreActions = () => {
    this.resetAllCallbacks();
    registerAllStoreActions();
  };

  run = {
    onAfterDeletedElement: (args: OnAfterDeletedElement) => {
      this.callbacks.immediateOnAfterDeletedElement.forEach(callback =>
        callback({ ...args })
      );
      forceNextTick(() => {
        this.callbacks.onAfterDeletedElement.forEach(callback =>
          callback({ ...args })
        );
      });
    },

    onBeforeDeletingElement: ({ elementId }: OnBeforeDeletingElement) => {
      // This is called immediately. There's no paint happening yet, instead we want to do something before painting.
      this.callbacks.onBeforeDeletingElement.forEach(callback =>
        callback({ elementId })
      );
    },

    onPropertyChange: (args: OnPropertyChange) => {
      this.callbacks.immediateOnPropertyChange.forEach(callback =>
        callback({ ...args })
      );
      forceNextTick(() => {
        this.callbacks.onPropertyChange.forEach(callback =>
          callback({ ...args })
        );
      });
    },

    onMovedElement: (args: OnMovedElement) => {
      this.callbacks.immediateOnMovedElement.forEach(callback =>
        callback({ ...args })
      );
      forceNextTick(() => {
        this.callbacks.onMovedElement.forEach(callback =>
          callback({ ...args })
        );
      });
    },

    onMountedElement: ({ elementId }: OnMountedElement) => {
      this.callbacks.immediateOnMountedElement.forEach(callback =>
        callback({ elementId })
      );
      forceNextTick(() => {
        this.callbacks.onMountedElement.forEach(callback =>
          requestIdleCallback(() => {
            callback({ elementId });
          })
        );
      });
    },

    onCreatedElement: (args: OnCreatedElement) => {
      this.callbacks.immediateOnCreatedElement.forEach(callback =>
        callback({ ...args })
      );

      forceNextTick(() => {
        this.callbacks.onCreatedElement.forEach(callback =>
          callback({ ...args })
        );
      });
    },

    onActivatedElement: ({ elementId }: OnActivatedElement) => {
      this.callbacks.onActivatedElement.forEach(callback =>
        callback({ elementId })
      );
    }
  };

  register = {
    onPropertyChange: (callback: OnPropertyChangeCallback) => {
      this.callbacks.onPropertyChange.push(callback);
    },

    immediateOnPropertyChange: (callback: OnPropertyChangeCallback) => {
      this.callbacks.immediateOnPropertyChange.push(callback);
    },

    onAfterDeletedElement: (callback: OnAfterDeletedElementCallback) => {
      this.callbacks.onAfterDeletedElement.push(callback);
    },

    immediateOnAfterDeletedElement: (
      callback: OnAfterDeletedElementCallback
    ) => {
      this.callbacks.immediateOnAfterDeletedElement.push(callback);
    },

    onMovedElement: (callback: OnMovedElementCallback) => {
      this.callbacks.onMovedElement.push(callback);
    },

    immediateOnMovedElement: (callback: OnMovedElementCallback) => {
      this.callbacks.immediateOnMovedElement.push(callback);
    },

    onMountedElement: (callback: OnMountedElementCallback) => {
      this.callbacks.onMountedElement.push(callback);
    },

    immediateOnMountedElement: (callback: OnMountedElementCallback) => {
      this.callbacks.immediateOnMountedElement.push(callback);
    },

    onCreatedElement: (callback: OnCreatedElementCallback) => {
      this.callbacks.onCreatedElement.push(callback);
    },

    immediateOnCreatedElement: (callback: OnCreatedElementCallback) => {
      this.callbacks.immediateOnCreatedElement.push(callback);
    },

    onBeforeDeletingElement: (callback: OnBeforeDeletingElementCallback) => {
      this.callbacks.onBeforeDeletingElement.push(callback);
    },

    onActivatedElement: (callback: OnActivatedElementCallback) => {
      this.callbacks.onActivatedElement.push(callback);
    }
  };

  /**
   * Should be called on beforeDestroy whenever you register an action
   */
  unregister(
    actionName: keyof StoreActionCallbacks,
    callback: AllCallbacksUnion
  ) {
    const index = this.callbacks[actionName].findIndex(
      (cb: AllCallbacksUnion) => cb === callback
    );

    if (index === -1) return;

    this.callbacks[actionName].splice(index, 1);
  }
}

export const breakdanceStoreActions = new BreakdanceStoreActions();
